import React, { useState, useEffect } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { url } from '../../util/config';
import { useNavigate } from 'react-router-dom';

const CreateUserComponent = () => {
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [mobile, setMobile] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [role, setRole] = useState('');
    const [roles, setRoles] = useState([]);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${url}/role`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!response.ok) throw new Error('Failed to fetch roles');
                const data = await response.json();
                setRoles(data || []);
            } catch (error) {
                console.error('Error fetching roles:', error);
                toast.error('Failed to load roles');
            }
        };
        fetchRoles();
    }, []);

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const indianMobileRegex = /^[6-9]\d{9}$/;

    const validate = (field, value) => {
        const newErrors = { ...errors };
        switch (field) {
            case 'name':
                if (!value) newErrors.name = 'Name is required.';
                else if (value.length < 3) newErrors.name = 'Name must be at least 3 characters.';
                else delete newErrors.name;
                break;
            case 'email':
                if (!value) newErrors.email = 'Email is required.';
                else if (!emailRegex.test(value)) newErrors.email = 'Please enter a valid email address.';
                else delete newErrors.email;
                break;
            case 'mobile':
                if (!value) newErrors.mobile = 'Mobile number is required.';
                else if (!indianMobileRegex.test(value)) newErrors.mobile = 'Please enter a valid 10-digit Indian mobile number.';
                else delete newErrors.mobile;
                break;
            case 'role':
                if (!value) newErrors.role = 'Please select a role.';
                else delete newErrors.role;
                break;
            case 'password':
                if (!value) newErrors.password = 'Password is required.';
                else if (!passwordRegex.test(value)) newErrors.password = 'Must be 6+ characters with uppercase, lowercase, number and special character (@$!%*?&).';
                else delete newErrors.password;
                break;
            default: break;
        }
        setErrors(newErrors);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate all fields on submit
        const submitErrors = {};
        if (!name) submitErrors.name = 'Name is required.';
        else if (name.length < 3) submitErrors.name = 'Name must be at least 3 characters.';
        if (!email) submitErrors.email = 'Email is required.';
        else if (!emailRegex.test(email)) submitErrors.email = 'Please enter a valid email address.';
        if (!mobile) submitErrors.mobile = 'Mobile number is required.';
        else if (!indianMobileRegex.test(mobile)) submitErrors.mobile = 'Please enter a valid 10-digit Indian mobile number.';
        if (!role) submitErrors.role = 'Please select a role.';
        if (!password) submitErrors.password = 'Password is required.';
        else if (!passwordRegex.test(password)) submitErrors.password = 'Must be 6+ characters with uppercase, lowercase, number and special character (@$!%*?&).';

        if (Object.keys(submitErrors).length > 0) {
            setErrors(submitErrors);
            return;
        }

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ name, email, mobile_no: mobile, password, country: '91', userrole: role }),
            });

            if (response.ok) {
                toast.success('User created successfully!', { autoClose: 3000 });
                setName(''); setEmail(''); setMobile(''); setPassword(''); setRole(''); setErrors({});
                setTimeout(() => navigate('/manageuser'), 3000);
            } else {
                const errorData = await response.json();
                toast.error(errorData.error || errorData.message || 'Failed to create user. Please try again.', { autoClose: 3000 });
            }
        } catch (error) {
            console.error('Error creating user:', error);
            toast.error('An error occurred. Please try again.', { autoClose: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <WrapperComponent>
            <div style={styles.page}>
                <div style={styles.card}>
                    {/* Card header */}
                    <div style={styles.cardHeader}>
                        <div style={styles.iconCircle}>
                            <i className="bi bi-person-plus" style={{ fontSize: '20px', color: '#1565c0' }}></i>
                        </div>
                        <div>
                            <h5 style={styles.cardTitle}>Create New User</h5>
                            <p style={styles.cardSubtitle}>Add a new user to your account</p>
                        </div>
                    </div>

                    <div style={styles.cardBody}>
                        <form onSubmit={handleSubmit} noValidate>
                            {/* Name */}
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>Full Name</label>
                                <input
                                    type="text"
                                    value={name}
                                    placeholder="Enter full name"
                                    style={{ ...styles.input, ...(errors.name ? styles.inputError : {}) }}
                                    onChange={(e) => setName(e.target.value)}
                                    onBlur={() => validate('name', name)}
                                />
                                {errors.name && <span style={styles.fieldError}>{errors.name}</span>}
                            </div>

                            {/* Email */}
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>Email Address</label>
                                <input
                                    type="email"
                                    value={email}
                                    placeholder="user@example.com"
                                    style={{ ...styles.input, ...(errors.email ? styles.inputError : {}) }}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onBlur={() => validate('email', email)}
                                />
                                {errors.email && <span style={styles.fieldError}>{errors.email}</span>}
                            </div>

                            {/* Mobile */}
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>Mobile Number</label>
                                <input
                                    type="text"
                                    value={mobile}
                                    placeholder="10-digit mobile number"
                                    maxLength="10"
                                    style={{ ...styles.input, ...(errors.mobile ? styles.inputError : {}) }}
                                    onChange={(e) => setMobile(e.target.value)}
                                    onBlur={() => validate('mobile', mobile)}
                                />
                                {errors.mobile && <span style={styles.fieldError}>{errors.mobile}</span>}
                            </div>

                            {/* Role */}
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>Role</label>
                                <select
                                    value={role}
                                    style={{ ...styles.input, ...(errors.role ? styles.inputError : {}) }}
                                    onChange={(e) => setRole(e.target.value)}
                                    onBlur={() => validate('role', role)}
                                >
                                    <option value="">Select a role</option>
                                    {roles.map((r) => (
                                        <option key={r._id} value={r._id}>{r.role_name}</option>
                                    ))}
                                </select>
                                {errors.role && <span style={styles.fieldError}>{errors.role}</span>}
                            </div>

                            {/* Password */}
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>Password</label>
                                <div style={styles.inputWrapper}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        placeholder="Set a strong password"
                                        style={{ ...styles.input, paddingRight: '40px', ...(errors.password ? styles.inputError : {}) }}
                                        onChange={(e) => setPassword(e.target.value)}
                                        onBlur={() => validate('password', password)}
                                    />
                                    <button type="button" style={styles.eyeBtn} onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                                        <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                    </button>
                                </div>
                                {errors.password && <span style={styles.fieldError}>{errors.password}</span>}
                            </div>

                            <p style={styles.hint}>
                                <i className="bi bi-info-circle" style={{ marginRight: 5 }}></i>
                                Password must be 6+ characters with uppercase, lowercase, a number and a special character (@$!%*?&).
                            </p>

                            {/* Actions */}
                            <div style={styles.actions}>
                                <button type="button" style={styles.btnSecondary} onClick={() => navigate('/manageuser')}>
                                    Cancel
                                </button>
                                <button type="submit" style={styles.btnPrimary} disabled={isLoading}>
                                    {isLoading
                                        ? <><span style={styles.spinner}></span> Creating…</>
                                        : 'Create User'}
                                </button>
                            </div>
                        </form>
                        <ToastContainer />
                    </div>
                </div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', justifyContent: 'center', paddingTop: '8px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    card: { background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', width: '100%', maxWidth: '520px', border: '1px solid #e5e7eb', overflow: 'hidden' },
    cardHeader: { display: 'flex', alignItems: 'center', gap: '14px', padding: '20px 24px', borderBottom: '1px solid #f3f4f6', background: '#f8fafc' },
    iconCircle: { width: '44px', height: '44px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    cardTitle: { margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' },
    cardSubtitle: { margin: 0, fontSize: '13px', color: '#6b7280' },
    cardBody: { padding: '24px' },
    fieldGroup: { marginBottom: '18px', display: 'flex', flexDirection: 'column', gap: '5px' },
    label: { fontSize: '13px', fontWeight: '600', color: '#374151' },
    inputWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
    input: { padding: '10px 12px', fontSize: '14px', border: '1.5px solid #d1d5db', borderRadius: '8px', outline: 'none', width: '100%', boxSizing: 'border-box', color: '#111827', fontFamily: 'inherit' },
    inputError: { borderColor: '#ef4444' },
    eyeBtn: { position: 'absolute', right: '10px', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '15px', padding: 0, display: 'flex', alignItems: 'center' },
    fieldError: { fontSize: '12px', color: '#ef4444' },
    hint: { fontSize: '12px', color: '#9ca3af', marginBottom: '20px', lineHeight: '1.5' },
    actions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    btnSecondary: { padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    spinner: { width: '15px', height: '15px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' },
};

export default CreateUserComponent;
